import React from "react";
import {
  Clock,
  CircleCheck,
  CircleDot,
  CircleX,
  Hourglass,
  UserRound,
} from "lucide-react";
import type { Shift } from "../types";
import { formatTimeInZone } from "../utils/formatShiftTime";

interface TimeDetailCardsProps {
  shift: Shift;
}

export function TimeDetailCards({ shift }: TimeDetailCardsProps) {
  const hasArrivalRejection =
    shift.arrival_time_rejected_at != null || shift.arrival_time_rejected_by != null;
  const hasArrivalApproval = shift.arrival_time_approved_by != null;
  const hasArrivalPending =
    shift.manual_arrival_time != null &&
    !hasArrivalRejection &&
    !hasArrivalApproval;
  const hasManagerArrival =
    shift.manager_arrival_time != null && shift.manager_arrival_time_set_by != null;

  const hasEndRejection =
    shift.end_time_rejected_at != null || shift.end_time_rejected_by != null;
  const hasEndApproval = shift.end_time_approved_by != null;
  const hasEndPending =
    shift.manual_end_time != null && !hasEndRejection && !hasEndApproval;
  const hasManagerEnd =
    shift.manager_end_time != null && shift.manager_end_time_set_by != null;

  return (
    <section id="time-details" className="px-5 space-y-4 mb-6">
      {/* Clock In Times Card */}
      <div
        id="clock-in-card"
        className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 my-1">
            <Clock className="h-4 w-4 text-primary" />
            Clock In Times
          </h3>
        </div>

        <div className="space-y-3">
          {/* App submitted (started_at) */}
          <div className="border border-gray-200 rounded-xl p-3">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Submitted
              </span>
              <span className="text-sm font-bold text-gray-800">
                {formatTimeInZone(shift.started_at, shift.timezone)}
              </span>
            </div>
          </div>

          {/* Manual arrival request - rejected */}
          {shift.manual_arrival_time && hasArrivalRejection && (
            <div className="border border-red-200 bg-red-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-red-600 uppercase tracking-wide flex items-center gap-1">
                  <CircleX className="h-3 w-3" />
                  Manual Request (Rejected)
                </span>
                <span className="text-sm font-bold text-red-700">
                  {formatTimeInZone(shift.manual_arrival_time, shift.timezone)}
                </span>
              </div>
              {shift.arrival_time_rejection_reason && (
                <div className="bg-red-100 border border-red-200 rounded-lg p-2 mt-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">
                    Manager Comment:
                  </p>
                  <p className="text-xs text-red-700">
                    &quot;{shift.arrival_time_rejection_reason}&quot;
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Manual arrival request - approved */}
          {shift.manual_arrival_time && hasArrivalApproval && !hasArrivalRejection && (
            <div className="border border-green-200 bg-green-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-green-600 uppercase tracking-wide flex items-center gap-1">
                  <CircleCheck className="h-3 w-3" />
                  Manual Request (Approved)
                </span>
                <span className="text-sm font-bold text-green-700">
                  {formatTimeInZone(shift.manual_arrival_time, shift.timezone)}
                </span>
              </div>
            </div>
          )}

          {/* Manual arrival request - pending */}
          {hasArrivalPending && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1">
                  <CircleDot className="h-3 w-3" />
                  Manual Request (Pending)
                </span>
                <span className="text-sm font-bold text-amber-700">
                  {formatTimeInZone(shift.manual_arrival_time, shift.timezone)}
                </span>
              </div>
            </div>
          )}

          {/* Manager arrival override */}
          {hasManagerArrival && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide flex items-center gap-1">
                  <UserRound className="h-3 w-3" />
                  Manager Override
                </span>
                <span className="text-sm font-bold text-blue-700">
                  {formatTimeInZone(shift.manager_arrival_time, shift.timezone)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clock Out Times Card */}
      <div
        id="clock-out-card"
        className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 my-1">
            <Hourglass className="h-4 w-4 text-red-500" />
            Clock Out Times
          </h3>
        </div>

        <div className="space-y-3">
          {/* App submitted (ended_at) */}
          {shift.ended_at && (
            <div className="border border-gray-200 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Submitted
                </span>
                <span className="text-sm font-bold text-gray-800">
                  {formatTimeInZone(shift.ended_at, shift.timezone)}
                </span>
              </div>
            </div>
          )}

          {/* Manual end request - rejected */}
          {shift.manual_end_time && hasEndRejection && (
            <div className="border border-red-200 bg-red-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-red-600 uppercase tracking-wide flex items-center gap-1">
                  <CircleX className="h-3 w-3" />
                  Manual Request (Rejected)
                </span>
                <span className="text-sm font-bold text-red-700">
                  {formatTimeInZone(shift.manual_end_time, shift.timezone)}
                </span>
              </div>
              {shift.end_time_rejection_reason && (
                <div className="bg-red-100 border border-red-200 rounded-lg p-2 mt-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">
                    Manager Comment:
                  </p>
                  <p className="text-xs text-red-700">
                    &quot;{shift.end_time_rejection_reason}&quot;
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Manual end request - approved */}
          {shift.manual_end_time && hasEndApproval && !hasEndRejection && (
            <div className="border border-green-200 bg-green-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-green-600 uppercase tracking-wide flex items-center gap-1">
                  <CircleCheck className="h-3 w-3" />
                  Manual Request (Approved)
                </span>
                <span className="text-sm font-bold text-green-700">
                  {formatTimeInZone(shift.manual_end_time, shift.timezone)}
                </span>
              </div>
            </div>
          )}

          {/* Manual end request - pending */}
          {hasEndPending && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1">
                  <CircleDot className="h-3 w-3" />
                  Manual Request (Pending)
                </span>
                <span className="text-sm font-bold text-amber-700">
                  {formatTimeInZone(shift.manual_end_time, shift.timezone)}
                </span>
              </div>
            </div>
          )}

          {/* Manager end override */}
          {hasManagerEnd && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide flex items-center gap-1">
                  <UserRound className="h-3 w-3" />
                  Manager Override
                </span>
                <span className="text-sm font-bold text-blue-700">
                  {formatTimeInZone(shift.manager_end_time, shift.timezone)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
